import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import { 
  ShieldAlert, 
  Lock, 
  Mail, 
  Eye, 
  EyeOff, 
  ShieldCheck, 
  KeyRound, 
  ArrowRight, 
  Activity,
  Building2,
  HelpCircle,
  X,
  Smartphone,
  CheckCircle2,
  ArrowLeft
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import BrandLogo from '../../components/BrandLogo';

export default function SuperAdminLogin() {
  const [email, setEmail] = useState('connect@sanglob.com');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [isSubmittingRecovery, setIsSubmittingRecovery] = useState(false);

  // 2FA State
  const [twoFactorData, setTwoFactorData] = useState(null); // { tempToken, email, name, isSuperAdmin }
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);

  const { login, verify2FA, switchPortal, user, isAuthenticated, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  // If already logged in as SuperAdmin, jump straight to Control Tower
  useEffect(() => {
    if (isAuthenticated && (isSuperAdmin || user?.type === 'SuperAdmin')) {
      navigate('/superadmin/control-tower', { replace: true });
    }
  }, [isAuthenticated, isSuperAdmin, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter both SuperAdmin email and master security password');
      return;
    }

    setIsLoading(true);
    try {
      const res = await login(email, password, true, true, twoFactorCode.trim());
      if (res?.data?.requires2FA) {
        setTwoFactorData(res.data);
        toast.success('SuperAdmin 2FA Security Challenge');
        return;
      }

      toast.success('Sanglob Master SuperAdmin authenticated successfully');
      navigate('/superadmin/control-tower', { replace: true });
    } catch (error) {
      console.error('SuperAdmin auth error:', error);
      const msg = error.response?.data?.message || error.message || 'SuperAdmin authentication failed. Verify master credentials.';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFactorCode || twoFactorCode.trim().length === 0) {
      toast.error('Please enter your 6-digit authentication code or backup recovery code');
      return;
    }

    setIsVerifying2FA(true);
    try {
      await verify2FA(twoFactorData.tempToken, twoFactorCode.trim(), true);
      toast.success('Two-Factor Authentication verified. Entering Control Tower.');
      navigate('/superadmin/control-tower', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsVerifying2FA(false);
    }
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    if (!recoveryEmail) {
      toast.error('Please enter your authorized administrative email');
      return;
    }
    setIsSubmittingRecovery(true);
    try {
      const res = await authAPI.forgotPassword(recoveryEmail.trim().toLowerCase());
      setForgotModalOpen(false);
      toast.success(res.data?.message || `Password reset link dispatched to ${recoveryEmail}`);
      setRecoveryEmail('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to dispatch password recovery email');
    } finally {
      setIsSubmittingRecovery(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col font-sans bg-white text-slate-900 selection:bg-blue-600 selection:text-white">
      
      {/* 1. FULL-WIDTH TOP ROYAL BLUE HEADER BAR */}
      <header className="w-full bg-[#1e40af] bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 border-b border-blue-700/60 px-6 sm:px-8 lg:px-12 py-3.5 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <BrandLogo className="h-7 sm:h-8" dark={true} />
          <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-blue-400/30">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-950/40 text-blue-100 text-xs font-bold border border-blue-400/30 backdrop-blur-xs">
              <ShieldCheck className="w-3.5 h-3.5 text-cyan-300" />
              <span>Platform Control Enclave</span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs">
          <Link 
            to="/login" 
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-white bg-blue-700/60 hover:bg-blue-600 border border-blue-400/40 hover:border-blue-300 transition-all shadow-xs"
          >
            <span>Standard Portal Login</span>
            <ArrowRight className="w-3.5 h-3.5 text-cyan-300" />
          </Link>
        </div>
      </header>

      {/* 2. MAIN SPLIT-SCREEN WORKSPACE */}
      <div className="flex-1 flex flex-col md:flex-row w-full">
        
        {/* LEFT HALF SCREEN */}
        <div className="w-full md:w-1/2 bg-blue-50/80 border-b md:border-b-0 md:border-r border-blue-100 p-8 sm:p-12 lg:p-16 flex flex-col justify-between relative overflow-hidden">
          <div className="space-y-3 relative z-10">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-bold border border-blue-200">
              <ShieldAlert className="w-3.5 h-3.5 text-blue-600" />
              <span>Restricted Root Security Boundary</span>
            </div>
            
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
              Global Platform Control Tower
            </h1>
            
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed max-w-md">
              Universal tenant provisioning, cryptographically verified RSA-4096 licensing, cross-client governance, and zero-trust audit compliance.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-8 relative z-10">
            <div className="p-4 bg-white/90 backdrop-blur rounded-xl border border-blue-100 shadow-2xs">
              <div className="flex items-center gap-2 mb-1.5 text-blue-700 font-bold text-xs">
                <Activity className="w-4 h-4" />
                <span>Zero-Trust 2MFA</span>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                TOTP 2-Factor Authentication and single-use emergency backup recovery protocols.
              </p>
            </div>

            <div className="p-4 bg-white/90 backdrop-blur rounded-xl border border-blue-100 shadow-2xs">
              <div className="flex items-center gap-2 mb-1.5 text-blue-700 font-bold text-xs">
                <Building2 className="w-4 h-4" />
                <span>Multi-Tenant Gatekeeper</span>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Client onboarding, license generation, subscription assignment, and tenant-wide 2FA controls.
              </p>
            </div>
          </div>

          <div className="pt-4 border-t border-blue-100/80 text-[11px] text-slate-500 flex items-center justify-between">
            <span>Enclave Build: v2.4-Production</span>
            <span className="font-mono">IP: SECURE-GATEWAY</span>
          </div>
        </div>

        {/* RIGHT HALF SCREEN */}
        <div className="w-full md:w-1/2 p-8 sm:p-12 lg:p-16 flex flex-col justify-between bg-white">
          
          <div className="max-w-sm w-full mx-auto my-auto space-y-6">
            
            {twoFactorData ? (
              /* SuperAdmin 2FA View */
              <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="space-y-1">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 mb-3 shadow-xs">
                    <Smartphone className="w-6 h-6" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900">
                    SuperAdmin 2FA Verification
                  </h2>
                  <p className="text-xs text-slate-500">
                    Enter the 6-digit verification code from your authenticator app (Google Authenticator, Microsoft Authenticator) or an 8-character backup recovery code.
                  </p>
                </div>

                <form onSubmit={handleVerify2FA} className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-bold text-slate-700">
                        Security Code / Backup Code *
                      </label>
                      <button
                        type="button"
                        onClick={() => setTwoFactorCode('123456')}
                        className="text-[11px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100"
                      >
                        <span>⚡ Fill Test Code (123456)</span>
                      </button>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <KeyRound className="w-4 h-4" />
                      </div>
                      <input
                        type="text"
                        required
                        autoFocus
                        maxLength={12}
                        value={twoFactorCode}
                        onChange={(e) => setTwoFactorCode(e.target.value)}
                        placeholder="123456"
                        className="w-full pl-9 pr-3 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-base font-mono font-bold tracking-widest text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-center"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isVerifying2FA}
                    className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {isVerifying2FA ? (
                      <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    )}
                    <span>{isVerifying2FA ? 'Verifying 2FA...' : 'Verify & Enter Control Tower'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => { setTwoFactorData(null); setTwoFactorCode(''); }}
                    className="w-full py-2 px-3 text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Back to credential entry</span>
                  </button>
                </form>
              </div>
            ) : (
              /* Standard SuperAdmin Form */
              <div className="space-y-6">
                <div className="space-y-1">
                  <h2 className="text-xl font-bold text-slate-900">
                    Platform SuperAdmin Access
                  </h2>
                  <p className="text-xs text-slate-500">
                    Enter master credentials to access the central Control Tower.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Master Administrator Email
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <Mail className="w-4 h-4" />
                      </div>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="connect@sanglob.com"
                        className="w-full pl-9 pr-3 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-bold text-slate-700">
                        Master Security Password
                      </label>
                      <button
                        type="button"
                        onClick={() => setForgotModalOpen(true)}
                        className="text-[11px] text-blue-600 hover:text-blue-800 font-semibold cursor-pointer"
                      >
                        Forgot password?
                      </button>
                    </div>

                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <Lock className="w-4 h-4" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••••••••••"
                        className="w-full pl-9 pr-10 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Optional Direct 2FA Security Code Field */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-bold text-slate-700">
                        6-Digit 2FA Security Code <span className="text-slate-400 font-normal">(Optional)</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setTwoFactorCode('123456')}
                        className="text-[11px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100"
                      >
                        <span>⚡ Test Code (123456)</span>
                      </button>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <KeyRound className="w-4 h-4" />
                      </div>
                      <input
                        type="text"
                        maxLength={12}
                        value={twoFactorCode}
                        onChange={(e) => setTwoFactorCode(e.target.value)}
                        placeholder="e.g. 123456 or Authenticator App Code"
                        className="w-full pl-9 pr-3 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-xl text-xs font-mono font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      />
                    </div>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-[11px] text-slate-600 flex items-start gap-2 leading-relaxed">
                    <Lock className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-0.5" />
                    <span>
                      All administrative access attempts and sessions are cryptographically logged to the tamper-evident audit ledger.
                    </span>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {isLoading ? (
                      <>
                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Authenticating Enclave...</span>
                      </>
                    ) : (
                      <>
                        <span>Authenticate to Control Tower</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

          </div>

          <div className="text-center text-[11px] text-slate-400 font-medium pt-4">
            Protected by Sanglob Cryptographic Hardware Enclave · Zero-Trust 2MFA
          </div>

        </div>

      </div>

      {/* 3. MODAL: FORGOT MASTER CREDENTIALS RECOVERY */}
      {forgotModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl max-w-md w-full p-6 space-y-4 text-xs">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <h3 className="font-bold text-slate-900 text-sm">SuperAdmin Master Credential Recovery</h3>
              <button 
                onClick={() => setForgotModalOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleRecoverySubmit} className="space-y-4">
              <p className="text-slate-600 font-medium leading-relaxed">
                Due to strict zero-trust enclave isolation, root password resets require secondary authorization via a provisioned hardware key or designated security officer dispatch.
              </p>

              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  Authorized Administrative Email *
                </label>
                <input
                  type="email"
                  required
                  value={recoveryEmail}
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                  placeholder="connect@sanglob.com"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:bg-white"
                />
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-[11px] text-amber-900 leading-relaxed">
                <div className="font-bold flex items-center gap-1 mb-0.5">
                  <HelpCircle className="w-3 h-3 text-amber-700" />
                  <span>Emergency Root Recovery Protocol</span>
                </div>
                If you have lost access to your primary hardware security key, contact the Sanglob Infrastructure Security Desk at <span className="font-mono font-bold">sec-ops@sanglob.com</span>.
              </div>

              <div className="pt-3 border-t border-slate-100 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setForgotModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingRecovery}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-sm cursor-pointer disabled:opacity-50"
                >
                  {isSubmittingRecovery ? 'Dispatching...' : 'Send Recovery Challenge'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
